/** Visits on agreed work: data calls, UK time conversion and calendar export for the website. */
export const defaultVisitTimeZone = 'Europe/London';
export const maxVisitOptions = 3;
export const visitPurposes = [
  { value: 'quote', label: 'Quote visit' },
  { value: 'work', label: 'Carry out the work' },
  { value: 'follow_up', label: 'Follow-up visit' },
];
export const purposeLabel = purpose => visitPurposes.find(option => option.value === purpose)?.label || 'Visit';

const messages = [
  ['VISIT_CONFLICT', 'Your company already has a confirmed visit at this time.'],
  ['VISIT_BLOCKED', 'Visits cannot be arranged while one of you has blocked the other.'],
  ['VISIT_FORBIDDEN', 'This visit is no longer available to this account.'],
  ['VISIT_SAME_SIDE', 'The other side needs to accept this time.'],
  ['VISIT_NOT_PENDING', 'This proposal has already been answered. Reload the page.'],
  ['VISIT_PROPOSAL_PENDING', 'There is already a proposal waiting for an answer. Answer or withdraw it first.'],
  ['VISIT_SLOT_IN_PAST', 'That time has already passed. Choose a time in the future.'],
  ['VISIT_SLOT_TOO_LONG', 'A visit can last at most 12 hours.'],
  ['VISIT_SLOT_TOO_FAR', 'Choose a time within the next year.'],
  ['VISIT_SLOT_INVALID', 'Each option needs an end time after its start time.'],
  ['VISIT_SLOT_DUPLICATE', 'Two of your options are the same time.'],
  ['VISIT_SLOT_NOT_FOUND', 'That time option is no longer available. Reload the page.'],
  ['VISIT_SLOTS_REQUIRED', 'Offer between 1 and 3 time options.'],
  ['RESCHEDULE_TARGET_INVALID', 'This visit can no longer be rescheduled. Reload the page.'],
  ['CANCEL_REASON_REQUIRED', 'Please give a reason.'],
  ['CANCEL_REASON_TOO_LONG', 'Please keep the reason under 500 characters.'],
  ['VISIT_NOT_CANCELLABLE', 'This visit has already been cancelled or completed.'],
  ['SIGN_IN_REQUIRED', 'Sign in to manage visits.'],
];
function check(error) {
  if (!error) return;
  const match = messages.find(([code]) => String(error.message || '').includes(code));
  const failure = new Error(match ? match[1] : error.message);
  if (match?.[0] === 'VISIT_CONFLICT') failure.conflict = true;
  throw failure;
}

export async function listWorkVisits(client, workId) {
  const { data, error } = await client.rpc('list_work_visits', { p_work_id: workId }); check(error); return Array.isArray(data) ? data : [];
}
export async function proposeVisit(client, { workId, purpose, slots, timeZone = defaultVisitTimeZone, rescheduleOf = null, note = '' }) {
  if (!Array.isArray(slots) || slots.length < 1 || slots.length > maxVisitOptions) throw new Error('Offer between 1 and 3 time options.');
  const { data, error } = await client.rpc('propose_visit', {
    p_work_id: workId, p_purpose: purpose, p_timezone: timeZone, p_reschedule_of: rescheduleOf, p_note: note.trim() || null,
    p_slots: slots.map(slot => ({ start: slot.start.toISOString(), end: slot.end.toISOString() })),
  }); check(error); return data;
}
export async function confirmVisitSlot(client, visitId, slotId, allowConflict = false) {
  const { data, error } = await client.rpc('confirm_visit_slot', { p_visit_id: visitId, p_slot_id: slotId, p_allow_conflict: allowConflict }); check(error); return data;
}
export async function cancelVisit(client, visitId, reason) {
  const trimmed = String(reason || '').trim();
  if (!trimmed) throw new Error('Please give a reason.');
  const { data, error } = await client.rpc('cancel_visit', { p_visit_id: visitId, p_reason: trimmed }); check(error); return data;
}

const pad = value => String(value).padStart(2, '0');
function zonedParts(date, timeZone) {
  const parts = new Intl.DateTimeFormat('en-GB', { timeZone, hourCycle: 'h23', year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' }).formatToParts(date);
  const get = type => Number(parts.find(part => part.type === type)?.value ?? 0);
  return { y: get('year'), m: get('month'), d: get('day'), hour: get('hour') % 24, minute: get('minute') };
}
export function todayIn(timeZone, now = new Date()) { const p = zonedParts(now, timeZone); return `${p.y}-${pad(p.m)}-${pad(p.d)}`; }
export function addDays(day, days) {
  const [y, m, d] = day.split('-').map(Number); const next = new Date(Date.UTC(y, m - 1, d + days));
  return `${next.getUTCFullYear()}-${pad(next.getUTCMonth() + 1)}-${pad(next.getUTCDate())}`;
}
/** Instant when the clock in timeZone shows day at "HH:MM". Null when the clocks skip that time; first occurrence when it repeats. */
export function localToUtc(day, time, timeZone) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day || '') || !/^\d{2}:\d{2}$/.test(time || '')) return null;
  const [y, m, d] = day.split('-').map(Number); const [hh, mm] = time.split(':').map(Number);
  const wanted = Date.UTC(y, m - 1, d, hh, mm);
  const wall = instant => { const p = zonedParts(new Date(instant), timeZone); return Date.UTC(p.y, p.m - 1, p.d, p.hour, p.minute); };
  const candidates = [wanted - (wall(wanted - 43200000) - (wanted - 43200000)), wanted - (wall(wanted + 43200000) - (wanted + 43200000))]
    .filter(instant => wall(instant) === wanted).sort((a, b) => a - b);
  return candidates.length ? new Date(candidates[0]) : null;
}
/** UTC range for a draft option, or a plain-English error. */
export function slotRange(draft, timeZone = defaultVisitTimeZone, now = new Date()) {
  const start = localToUtc(draft.day, draft.time, timeZone);
  if (!start) return { error: 'Choose a valid date and start time. Some times do not exist on the day the clocks go forward.' };
  const minutes = Number(draft.minutes);
  if (!Number.isFinite(minutes) || minutes <= 0 || minutes > 720) return { error: 'A visit can last at most 12 hours.' };
  if (start.getTime() <= now.getTime()) return { error: 'That time has already passed. Choose a time in the future.' };
  return { start, end: new Date(start.getTime() + minutes * 60000) };
}

const weekdays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
export function formatDay(day) {
  const [y, m, d] = day.split('-').map(Number);
  return `${weekdays[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d} ${months[m - 1]}`;
}
/** "Tuesday 29 September, 09:00 to 11:00" from the server's local-time strings. */
export function formatLocalRange(localStart, localEnd) {
  if (!localStart || !localEnd) return 'Time not set';
  const [startDay, startTime] = localStart.split('T'); const [endDay, endTime] = localEnd.split('T');
  return startDay === endDay ? `${formatDay(startDay)}, ${startTime} to ${endTime}` : `${formatDay(startDay)}, ${startTime} to ${formatDay(endDay)}, ${endTime}`;
}

const icsStamp = iso => new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
const icsText = value => String(value).replace(/\\/g, '\\\\').replace(/\n/g, '\\n').replace(/[,;]/g, match => `\\${match}`);
/** One-event calendar file for a confirmed visit, in UTC so every calendar app shows the right time. */
export function buildVisitIcs({ id, title, start, end, description = '', now = new Date() }) {
  return ['BEGIN:VCALENDAR', 'VERSION:2.0', 'PRODID:-//Resident Service 360//Visits//EN', 'CALSCALE:GREGORIAN', 'METHOD:PUBLISH', 'BEGIN:VEVENT',
    `UID:${id}@residentservice360`, `DTSTAMP:${icsStamp(now.toISOString())}`, `DTSTART:${icsStamp(start)}`, `DTEND:${icsStamp(end)}`,
    `SUMMARY:${icsText(title)}`, ...(description ? [`DESCRIPTION:${icsText(description)}`] : []), 'END:VEVENT', 'END:VCALENDAR', ''].join('\r\n');
}
